<template>
    <div class="phone-input">
        <select v-model="country" @change="emitValue()" :disabled="disabled" title="Country">
            <!-- The selected option shows just "US +1" so the closed dropdown stays narrow; the list keeps full names -->
            <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.code == country ? `${c.code} (+${c.dial})` : `${c.name} (+${c.dial})` }}</option>
        </select>
        <input type="tel" v-model="text" @input="onInput()" :placeholder="placeholder" :disabled="disabled" autocomplete="tel" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { phoneCountries } from '@/phone';

const COUNTRIES = phoneCountries();

// Country picker + number field. v-model is the E.164 number ("+15135551234") once the entry is a
// complete, valid number; until then it's the raw text, so check it with isValidPhone() before saving.
export default defineComponent({
    name: 'PhoneInput',
    props: {
        modelValue: { type: String, default: '' },
        placeholder: { type: String, default: 'Phone Number' },
        disabled: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            countries: COUNTRIES,
            country: 'US' as any,
            text: '',
            lastEmitted: null as any
        }
    },
    created() {
        this.load(this.modelValue);
    },
    watch: {
        modelValue(value: string) {
            // Only reload for changes made outside this input (e.g. the form being reset)
            if(value !== this.lastEmitted) {
                this.load(value);
            }
        }
    },
    methods: {
        load(value: string) {
            if(!value) {
                this.text = '';
                return;
            }
            const phone = parsePhoneNumberFromString(String(value), this.country);
            if(phone && phone.isValid()) {
                this.country = phone.country || this.country;
                this.text = phone.formatNational();
            } else {
                this.text = String(value);
            }
        },
        onInput() {
            // A pasted/typed international number ("+44 ...") picks its own country
            if(this.text.trim().startsWith('+')) {
                const phone = parsePhoneNumberFromString(this.text.trim());
                if(phone && phone.country) {
                    this.country = phone.country;
                }
            }
            this.emitValue();
        },
        emitValue() {
            const phone = parsePhoneNumberFromString(this.text.trim(), this.country);
            const value = phone && phone.isValid() ? phone.number as string : this.text.trim();
            this.lastEmitted = value;
            this.$emit('update:modelValue', value);
        }
    }
});
</script>

<style scoped>
.phone-input { display: flex; gap: 6px; }
/* style.scss gives every select inside a <form> 40px right padding (!important); only room for the arrow is needed here */
.phone-input select { width: 120px; flex-shrink: 0; padding-right: 10px !important; margin-right: 0!important }
.phone-input input { flex: 1; min-width: 0; }
</style>
